/** Dashboard — v1 parity (permits-roi.js dashboardView + buyers-agent.js
 * updateSidebarStats): KPI stats (deals / open tasks / pipeline $ from
 * purchase prices, v1's $X.XM|$Xk format), the Deal Pipeline bars per
 * TXN_COLS column, and My Tasks grouped Property / Transaction / Wholesale
 * via v1's taskCategory (street-key matching, ported verbatim). v1's
 * "Recent Activity" feeds off the blob activity log v2 doesn't read — the
 * card here shows the newest leads instead (matrix-noted). Quick actions
 * navigate; creation flows stay where they live. */
import { useMemo } from "react";
import { fmtMoney, type Lead } from "../../types";
import { isTaskDone, parseMoneyEdit, type TaskRec } from "../edits/api";
import { STAGE_NAMES, stageName, type Deal } from "../deals/api";
import type { WholesaleDeal } from "../wholesale/api";

/** v1 _streetKey: address up to the first comma, lowercased. */
export const streetKey = (a: string | null): string =>
  String(a ?? "").split(",")[0]!.trim().toLowerCase();

/** v1 taskCategory(t): wholesale if the deal address is a wholesale txn;
 * transaction if auto/stage set or the address is a (non-wholesale) deal;
 * else property. */
export function taskCategory(
  t: TaskRec & { auto?: boolean; stage?: string | null },
  whKeys: Set<string>,
  txnKeys: Set<string>,
): "property" | "transaction" | "wholesale" {
  const k = streetKey(t.deal);
  if (t.deal && whKeys.has(k)) return "wholesale";
  if (t.deal && txnKeys.has(k)) return "transaction";
  return "property";
}

/** v1 sidebar pipeline format: $1.2M / $850k / $900. */
export const fmtPipe = (n: number): string =>
  n >= 1e6 ? "$" + (n / 1e6).toFixed(1) + "M" : n >= 1e3 ? "$" + Math.round(n / 1e3) + "k" : "$" + n;

export function DashboardPage({
  leads,
  deals,
  tasks,
  wholesale,
  inboxUnread,
  onNav,
  onToggleTask,
  onOpenLead,
}: {
  leads: Lead[];
  deals: Deal[];
  tasks: TaskRec[];
  wholesale: WholesaleDeal[];
  inboxUnread: number;
  onNav: (page: "properties" | "tasks" | "inbox" | "deals" | "wholesale" | "contacts") => void;
  onToggleTask: (key: string, done: boolean) => void;
  onOpenLead: (address: string) => void;
}) {
  const stats = useMemo(() => {
    let pipe = 0;
    for (const d of deals) pipe += parseMoneyEdit(d.fields["t_purchase"]) ?? 0;
    const dayStart = new Date();
    dayStart.setHours(0, 0, 0, 0);
    const today = leads.filter((l) => l.createdAt >= dayStart.getTime()).length;
    const open = tasks.filter((t) => !isTaskDone(t.status));
    return { pipe, today, open };
  }, [leads, deals, tasks]);

  const pipeline = useMemo(() => {
    const counts = new Array<number>(STAGE_NAMES.length).fill(0);
    const extra = new Map<number, number>();
    for (const d of deals) {
      if (d.stageIdx >= 0 && d.stageIdx < counts.length) counts[d.stageIdx] = (counts[d.stageIdx] ?? 0) + 1;
      else extra.set(d.stageIdx, (extra.get(d.stageIdx) ?? 0) + 1);
    }
    const rows = counts.map((n, i) => ({ name: stageName(i), n }));
    for (const [i, n] of extra) rows.push({ name: stageName(i), n });
    const mx = Math.max(1, ...rows.map((r) => r.n));
    return { rows, mx };
  }, [deals]);

  const groups = useMemo(() => {
    const whKeys = new Set(wholesale.map((w) => streetKey(w.address)));
    const txnKeys = new Set(deals.map((d) => streetKey(d.address)));
    const open = tasks.filter((t) => !isTaskDone(t.status));
    const by = { property: [] as TaskRec[], transaction: [] as TaskRec[], wholesale: [] as TaskRec[] };
    for (const t of open) by[taskCategory(t, whKeys, txnKeys)].push(t);
    return by;
  }, [tasks, deals, wholesale]);

  const recent = useMemo(
    () => [...leads].sort((a, b) => b.createdAt - a.createdAt).slice(0, 6),
    [leads],
  );

  const taskRow = (t: TaskRec) => (
    <label key={t.natKey} className="taskrow slim">
      <input type="checkbox" checked={false} onChange={() => onToggleTask(t.natKey, true)} />
      <span className={"pridot p-" + (t.pri ? t.pri.toLowerCase() : "none")} aria-hidden="true" />
      <span className="taskbody">
        <span className="tasktitle">{t.title}</span>
        <span className="sub">
          {t.deal ?? "—"}{t.due ? ` · Due ${t.due}` : ""}{t.who ? ` · ${t.who}` : ""}
        </span>
      </span>
      {t.pri ? <span className={"pri pri-" + t.pri.toLowerCase()}>{t.pri.toLowerCase()}</span> : null}
    </label>
  );

  const sect = (title: string, arr: TaskRec[], hue: string) => (
    <div className="dsect" key={title}>
      <div className="dsecthead">
        <span className={"dsdot h-" + hue} aria-hidden="true" />
        {title} <span className="stagecount">{arr.length}</span>
      </div>
      {arr.length ? arr.slice(0, 4).map(taskRow) : <span className="sub none">None open</span>}
    </div>
  );

  const openTotal = groups.property.length + groups.transaction.length + groups.wholesale.length;

  return (
    <div className="dash">
      <div className="anlhero">
        <div className="anlherobrand">
          <span className="anlherobadge" aria-hidden="true">🏠</span>
          <div className="anlherotext">
            <h1>Dashboard</h1>
            <p>Overview of your fix &amp; flip portfolio</p>
          </div>
        </div>
        <div className="dashherostat">
          <span className="dhl">Pipeline</span>
          <strong>{fmtPipe(stats.pipe)}</strong>
        </div>
      </div>

      <div className="anlkpis">
        <button className="anlkpi" onClick={() => onNav("properties")}>
          <div className="anlkpitop">
            <span className="dkl">Leads</span>
            <span className="anlkpiico ico-blue" aria-hidden="true">🏠</span>
          </div>
          <div className="anlkpival">{leads.length.toLocaleString()}</div>
          <div className="anlkpisub">{stats.today ? `+${stats.today} today` : "—"}</div>
        </button>
        <button className="anlkpi" onClick={() => onNav("deals")}>
          <div className="anlkpitop">
            <span className="dkl">Deals</span>
            <span className="anlkpiico ico-violet" aria-hidden="true">📄</span>
          </div>
          <div className="anlkpival">{deals.length}</div>
          <div className="anlkpisub">pipeline {fmtPipe(stats.pipe)}</div>
        </button>
        <button className="anlkpi" onClick={() => onNav("tasks")}>
          <div className="anlkpitop">
            <span className="dkl">Open tasks</span>
            <span className="anlkpiico ico-amber" aria-hidden="true">✅</span>
          </div>
          <div className="anlkpival">{stats.open.length}</div>
          <div className="anlkpisub">{tasks.length - stats.open.length} done</div>
        </button>
        <button className="anlkpi" onClick={() => onNav("inbox")}>
          <div className="anlkpitop">
            <span className="dkl">Inbox</span>
            <span className="anlkpiico ico-emerald" aria-hidden="true">📨</span>
          </div>
          <div className="anlkpival">{inboxUnread || "—"}</div>
          <div className="anlkpisub">unread threads</div>
        </button>
      </div>

      <div className="dgrid">
        <div className="dcard">
          <div className="dhead">
            <h2>Deal Pipeline</h2>
            <span className="sub">{deals.length} total deals</span>
          </div>
          {pipeline.rows.map((r) => (
            <div key={r.name} className="pipebar">
              <span className="pipelbl">{r.name}</span>
              <div className="pipetrack">
                {r.n ? (
                  <div className="pipefill" style={{ width: Math.max((r.n / pipeline.mx) * 100, 8) + "%" }}>{r.n}</div>
                ) : (
                  <span className="pipezero">0</span>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="dcard">
          <div className="dhead">
            <h2>My Tasks</h2>
            <div className="dheadright">
              <span className="dopen">{openTotal} open</span>
              <button className="dlink" onClick={() => onNav("tasks")}>View all →</button>
            </div>
          </div>
          {openTotal === 0 ? (
            <div className="sub none">All caught up — no open tasks.</div>
          ) : (
            <>
              {sect("Properties", groups.property, "blue")}
              {sect("Transactions", groups.transaction, "violet")}
              {sect("Wholesale Transactions", groups.wholesale, "orange")}
            </>
          )}
        </div>

        <div className="dcard">
          <div className="dhead">
            <h2>Newest Leads</h2>
            <button className="dlink" onClick={() => onNav("properties")}>All leads →</button>
          </div>
          {recent.map((l) => (
            <button key={l.id} className="dlead" onClick={() => onOpenLead(l.address)}>
              <span className="dleadava" aria-hidden="true">{(l.source || "?").charAt(0).toUpperCase()}</span>
              <span className="dleadmain">
                <span className="dleadaddr">{l.address}</span>
                <span className="sub">
                  {l.source} · {fmtMoney(l.price)}
                  {l.createdAt ? " · " + new Date(l.createdAt).toLocaleDateString([], { month: "short", day: "numeric" }) : ""}
                </span>
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
